// Admin role permission matrix for the GCBC prototype.
//
// Every mutator surface in /admin should be gated by `canPerform(action, role)`.
// Reads are open to every signed-in admin (including Read-only Operator).
//
// Roles come from dataAdapter.js → normalized labels:
//   "Super Admin" · "Deal Manager" · "Payments Overseer" · "Read-only Operator"

const SUPER_ADMIN       = "Super Admin";
const DEAL_MANAGER      = "Deal Manager";
const PAYMENTS_OVERSEER = "Payments Overseer";
const READ_ONLY         = "Read-only Operator";

// Action → set of roles allowed to perform it. Super Admin is implicitly added.
const MATRIX = {
  // Deals
  "deals.create":           [DEAL_MANAGER],
  "deals.edit":             [DEAL_MANAGER],
  "deals.publish":          [DEAL_MANAGER],
  "deals.delete":           [DEAL_MANAGER],
  "deals.retroactive":      [DEAL_MANAGER],

  // Applications (member onboarding queue)
  "applications.approve":   [DEAL_MANAGER],
  "applications.reject":    [DEAL_MANAGER],

  // Card submissions
  "submissions.approve":    [DEAL_MANAGER],
  "submissions.reject":     [DEAL_MANAGER],
  "submissions.request":    [DEAL_MANAGER],

  // Payments / payouts
  "payments.generateRun":   [PAYMENTS_OVERSEER],
  "payments.export":        [PAYMENTS_OVERSEER],
  "payments.markSent":      [PAYMENTS_OVERSEER],
  "payments.markCompleted": [PAYMENTS_OVERSEER],
  "payments.markReturned":  [PAYMENTS_OVERSEER],
  "payments.markRefunded":  [PAYMENTS_OVERSEER],
  "payments.cancel":        [PAYMENTS_OVERSEER],

  // Members (account lifecycle)
  "members.suspend":        [],     // Super Admin only
  "members.reactivate":     [],
  "members.edit":           [],

  // Brands & rules
  "brands.create":          [DEAL_MANAGER],
  "brands.edit":            [DEAL_MANAGER],

  // Settings
  "settings.modify":        [],     // Super Admin only

  // Demo / prototype-only utilities — open to everyone signed in
  "demo.reset":             [DEAL_MANAGER, PAYMENTS_OVERSEER, READ_ONLY],
};

// Human-readable action labels for tooltips ("This action requires …").
const ACTION_LABELS = {
  "deals.create":           "Create deals",
  "deals.edit":             "Edit deals",
  "deals.publish":          "Publish or pause deals",
  "deals.delete":           "Delete deals",
  "deals.retroactive":      "Apply retroactive rate changes",
  "applications.approve":   "Approve applications & send KYC",
  "applications.reject":    "Reject applications",
  "submissions.approve":    "Approve card submissions",
  "submissions.reject":     "Reject card submissions",
  "submissions.request":    "Request a member response",
  "payments.generateRun":   "Generate a payment run",
  "payments.export":        "Decrypt & export payment runs",
  "payments.markSent":      "Mark payments as sent",
  "payments.markCompleted": "Mark payments as completed",
  "payments.markReturned":  "Record ACH returns",
  "payments.markRefunded":  "Record refunds",
  "payments.cancel":        "Cancel a payment item",
  "members.suspend":        "Suspend a member",
  "members.reactivate":     "Reactivate a member",
  "members.edit":           "Edit member records",
  "brands.create":          "Create brands",
  "brands.edit":            "Edit brand rules",
  "settings.modify":        "Modify system settings",
  "demo.reset":             "Reset demo state",
};

// Page-level access matrix. Routes not listed for a role redirect to /admin/dashboard
// with a clean 403 screen. Read-only Operator implicitly sees every page in
// read-mode (Gated wrappers already disable every mutator).
const ALL_PAGES = [
  "dashboard", "audit", "deals", "applications", "submissions",
  "brands", "payments", "members", "settings",
];

const PAGE_ACCESS = {
  [SUPER_ADMIN]:       new Set(ALL_PAGES),
  [DEAL_MANAGER]:      new Set(["dashboard", "audit", "deals", "applications", "submissions", "brands"]),
  [PAYMENTS_OVERSEER]: new Set(["dashboard", "audit", "payments"]),
  [READ_ONLY]:         new Set(ALL_PAGES.filter(p => p !== "settings")), // sees everything except Settings
};

// Map every route path under /admin to its access key. Detail routes inherit
// from their list route. Routes not in this map are treated as Super-only.
const ROUTE_TO_PAGE = [
  [/^\/admin\/dashboard$/,                 "dashboard"],
  [/^\/admin\/audit/,                      "audit"],
  [/^\/admin\/deals/,                      "deals"],
  [/^\/admin\/applications/,               "applications"],
  [/^\/admin\/submissions/,                "submissions"],
  [/^\/admin\/brands/,                     "brands"],
  [/^\/admin\/payments/,                   "payments"],
  [/^\/admin\/members/,                    "members"],
  [/^\/admin\/settings/,                   "settings"],
];

export function pageForPath(path) {
  const hit = ROUTE_TO_PAGE.find(([re]) => re.test(path));
  return hit ? hit[1] : null;
}

export function canVisitPage(page, role) {
  if (!page) return true;
  if (!role) return false;
  return PAGE_ACCESS[role]?.has(page) ?? false;
}

export function canVisitPath(path, role) {
  return canVisitPage(pageForPath(path), role);
}

export function canPerform(action, role) {
  if (!role) return false;
  if (role === SUPER_ADMIN) return true;
  const allowed = MATRIX[action];
  if (!allowed) return false;
  return allowed.includes(role);
}

// Custom error type so callers can distinguish denials from other failures.
export class PermissionDeniedError extends Error {
  constructor(action, role) {
    super(`Permission denied: ${action} (signed in as ${role || "anonymous"})`);
    this.name = "PermissionDeniedError";
    this.action = action;
    this.role = role;
  }
}

/**
 * assertCanPerform(action) — defensive check for use INSIDE mutator functions.
 *
 * Reads the live admin role from the SUPER_ADMIN proxy (re-pinned on sign-in)
 * and throws a PermissionDeniedError if the action isn't allowed. This is a
 * second layer of defense behind <Gated>: if a click handler is wired
 * incorrectly, a future codepath forgets <Gated>, or someone runs the mutator
 * from the browser console, the action is still blocked and a denial event
 * lands in the audit log.
 *
 * Returns the resolved role on success so the caller can log it.
 */
export function assertCanPerform(action) {
  const role = CURRENT_ADMIN?.role || null;
  if (!canPerform(action, role)) {
    throw new PermissionDeniedError(action, role);
  }
  return role;
}

// Returns the roles that COULD perform this action (excluding Super Admin,
// who is implicit). Used in the tooltip copy.
export function whoCanPerform(action) {
  const roles = MATRIX[action] || [];
  return [SUPER_ADMIN, ...roles];
}

export function actionLabel(action) {
  return ACTION_LABELS[action] || action;
}

// Convenience React hook — reads the currently signed-in admin's role.
import { SUPER_ADMIN as CURRENT_ADMIN } from "./mock-data";
export function useCurrentAdminRole() {
  return CURRENT_ADMIN?.role || SUPER_ADMIN;
}
